import moment from 'moment';
import { Bar, BarChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import { DeviceTelemetryBucket } from '../../hooks/useDeviceTelemetry';
import { GraphContainer, GraphDescription, GraphTitle } from './GraphParts';

export function GsmSignalTooltip({ active, payload }: {
  active?: boolean;
  payload?: Array<{ payload: DeviceTelemetryBucket }>;
}) {
  const bucket = payload?.[0]?.payload;
  if (!active || !bucket) return null;

  return (
    <div style={{ background: 'white', border: '1px solid #ccc', padding: 10, fontSize: 12 }}>
      <div>{moment(bucket.key).format('MMM D, HH:mm')}–{moment(bucket.key).add(15, 'minutes').format('HH:mm Z')}</div>
      {bucket.gsmSignalAverage === null ? <div>No readings</div> : <>
        <div>Average: {Number(bucket.gsmSignalAverage.toFixed(2))}</div>
        <div>Minimum: {bucket.gsmSignalMin}</div>
        <div>Maximum: {bucket.gsmSignalMax}</div>
        <div>Readings: {bucket.gsmSignalSampleCount}</div>
      </>}
    </div>
  );
}

// minPointSize makes a reported zero visible; missing buckets must stay empty.
export function GsmSignalBar({ x, y, width, height, payload }: {
  x?: number; y?: number; width?: number; height?: number; payload?: DeviceTelemetryBucket;
}) {
  if (payload?.gsmSignalAverage == null) return <g />;
  return <rect x={x} y={y} width={width} height={height} fill="#268b91" data-gsm-average={payload.gsmSignalAverage} />;
}

export function DeviceGsmSignalChart({ buckets, make, isLoading }: {
  buckets: DeviceTelemetryBucket[]; make: string; isLoading: boolean;
}) {
  const hasReadings = buckets.some(bucket => bucket.gsmSignalSampleCount > 0);
  if (make.toLowerCase() !== 'gosafe' && !hasReadings) return null;

  return (
    <GraphContainer data-testid="gsm-signal-chart">
      <GraphTitle>GSM signal</GraphTitle>
      {isLoading ? <p>Loading GSM signal…</p> : !hasReadings ?
        <p>No GSM signal readings in this period.</p> :
        <ResponsiveContainer width="100%" height={140}>
          <BarChart data={buckets} margin={{ top: 5, right: 5, bottom: 8, left: 0 }}>
            <XAxis dataKey="key" hide />
            <YAxis domain={[0, (max: number) => Math.max(1, max)]} width={40} tick={{ fontSize: 12 }} allowDecimals={false} axisLine={false} tickLine={false} />
            <Tooltip filterNull={false} content={<GsmSignalTooltip />} />
            <Bar dataKey="gsmSignalAverage" name="Raw GSM signal" minPointSize={2} shape={<GsmSignalBar />} isAnimationActive={false} />
          </BarChart>
        </ResponsiveContainer>
      }
      <GraphDescription>
        Average raw GSM signal per 15-minute bucket, including zero readings. Hover for minimum,
        maximum and sample count. Gaps mean no readings; a mark on the baseline means zero.
      </GraphDescription>
    </GraphContainer>
  );
}
